import { useCallback } from 'react'
import { useAnalyticsTrack } from '@/features/amplitude'
import type {
  ChangeTiming,
  PlanCadence,
  PlanChangeTier,
  PlanOptionsUnavailableReason,
  PromoCodeRejectionReason,
} from '../lib/planChange.types'
import {
  PLAN_CHANGE_EVENTS,
  type PromoSource,
} from './plan-change-analytics.constants'

type OptionsLoadedProps = {
  unavailableReason: PlanOptionsUnavailableReason | null
  suppressedTargetCount: number
  targetCount: number
  recommendedTier: PlanChangeTier | null
}

type TargetSelectedProps = {
  fromTier: PlanChangeTier
  fromCadence: PlanCadence
  toTier: PlanChangeTier
  toCadence: PlanCadence
  isRecommended: boolean
}

type ConfirmViewedProps = TargetSelectedProps & {
  timing: ChangeTiming
  endingTrial: boolean
  lossCount: number
  promoSource: PromoSource
}

type SubmittedProps = {
  toTier: PlanChangeTier
  toCadence: PlanCadence
  timing: ChangeTiming
  promoSource: PromoSource
}

type FailedProps = {
  status: number | null
  errorCode: string | null
}

type PendingCancelledProps = {
  fromTier: PlanChangeTier
  fromCadence: PlanCadence
}

type PromoCodeRejectedProps = {
  reason: PromoCodeRejectionReason
  stage: 'preview' | 'confirm'
}

type PlanChangeAnalytics = {
  trackOptionsLoaded: (props: OptionsLoadedProps) => void
  trackTargetSelected: (props: TargetSelectedProps) => void
  trackConfirmViewed: (props: ConfirmViewedProps) => void
  trackSubmitted: (props: SubmittedProps) => void
  trackFailed: (props: FailedProps) => void
  trackPendingCancelled: (props: PendingCancelledProps) => void
  trackPromoCodeEntered: () => void
  trackPromoCodeAccepted: () => void
  trackPromoCodeRejected: (props: PromoCodeRejectedProps) => void
}

export const usePlanChangeAnalytics = (): PlanChangeAnalytics => {
  const track = useAnalyticsTrack()

  const trackOptionsLoaded = useCallback(
    (props: OptionsLoadedProps) =>
      track(PLAN_CHANGE_EVENTS.OptionsLoaded, props),
    [track]
  )

  const trackTargetSelected = useCallback(
    (props: TargetSelectedProps) =>
      track(PLAN_CHANGE_EVENTS.TargetSelected, props),
    [track]
  )

  const trackConfirmViewed = useCallback(
    (props: ConfirmViewedProps) =>
      track(PLAN_CHANGE_EVENTS.ConfirmViewed, props),
    [track]
  )

  const trackSubmitted = useCallback(
    (props: SubmittedProps) => track(PLAN_CHANGE_EVENTS.Submitted, props),
    [track]
  )

  const trackFailed = useCallback(
    (props: FailedProps) => track(PLAN_CHANGE_EVENTS.Failed, props),
    [track]
  )

  const trackPendingCancelled = useCallback(
    (props: PendingCancelledProps) =>
      track(PLAN_CHANGE_EVENTS.PendingCancelled, props),
    [track]
  )

  const trackPromoCodeEntered = useCallback(
    () => track(PLAN_CHANGE_EVENTS.PromoCodeEntered),
    [track]
  )

  const trackPromoCodeAccepted = useCallback(
    () => track(PLAN_CHANGE_EVENTS.PromoCodeAccepted),
    [track]
  )

  const trackPromoCodeRejected = useCallback(
    (props: PromoCodeRejectedProps) =>
      track(PLAN_CHANGE_EVENTS.PromoCodeRejected, props),
    [track]
  )

  return {
    trackOptionsLoaded,
    trackTargetSelected,
    trackConfirmViewed,
    trackSubmitted,
    trackFailed,
    trackPendingCancelled,
    trackPromoCodeEntered,
    trackPromoCodeAccepted,
    trackPromoCodeRejected,
  }
}
